import type { PhotoDetail } from '@/types/photo';
import { AUTH_MODE } from '@/services/api';
import * as photoMock from '@/services/photoMock';
import * as photoApi from '@/services/photoApi';

export type PhotoSearchOptions = photoApi.PhotoSearchOptions;
export type PhotoUploadResult = photoApi.PhotoUploadResult;
export type PhotoUploadProgress = photoApi.PhotoUploadProgress;
export type PhotoUploadProgressHandler = photoApi.PhotoUploadProgressHandler;

export async function getPhotoById(id: string): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE === 'mock') return photoMock.getPhotoById(id);
  return photoApi.getPhotoById(id);
}

export async function getPhotosByAlbumId(albumId: string): Promise<PhotoDetail[]> {
  if (AUTH_MODE === 'mock') return photoMock.getPhotosByAlbumId(albumId);
  return photoApi.getPhotosByAlbumId(albumId);
}

export async function getAllPhotos(): Promise<PhotoDetail[]> {
  if (AUTH_MODE === 'mock') return photoMock.getAllPhotos();
  return photoApi.getRecentPhotos(50);
}

export async function downloadPhoto(id: string): Promise<{ blob: Blob; filename: string } | null> {
  if (AUTH_MODE === 'mock') return photoMock.downloadPhoto(id);
  return photoApi.downloadPhoto(id);
}

export async function downloadPhotosAsZip(
  photoIds: string[],
): Promise<{ blob: Blob; totalSize: number; filename: string } | null> {
  if (AUTH_MODE === 'mock') return photoMock.downloadPhotosAsZipBlob(photoIds);
  const result = await photoApi.downloadPhotosAsZip(photoIds);
  return { ...result, totalSize: result.blob.size };
}

export async function getRecentPhotos(count: number): Promise<PhotoDetail[]> {
  if (AUTH_MODE === 'mock') return photoMock.getRecentPhotos(count);
  return photoApi.getRecentPhotos(count);
}

export async function searchPhotos(options: PhotoSearchOptions = {}): Promise<PhotoDetail[]> {
  if (AUTH_MODE === 'mock') return photoMock.searchPhotos(options);
  return photoApi.searchPhotos(options);
}

export async function getFavoritePhotos(): Promise<PhotoDetail[]> {
  if (AUTH_MODE === 'mock') return photoMock.getFavoritePhotos();
  return photoApi.getFavoritePhotos();
}

export async function getLastYearTodayMemories(): Promise<{
  title: string;
  memoryDate: string;
  photos: PhotoDetail[];
}> {
  if (AUTH_MODE === 'mock') return photoMock.getLastYearTodayMemories();
  return photoApi.getLastYearTodayMemories();
}

export async function setPhotoFavorite(
  id: string,
  favorite: boolean,
): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE === 'mock') return photoMock.setPhotoFavorite(id, favorite);
  return photoApi.setPhotoFavorite(id, favorite);
}

export async function addPhoto(photo: PhotoDetail, blob: Blob): Promise<PhotoDetail> {
  if (AUTH_MODE === 'mock') return photoMock.addPhoto(photo, blob);

  const file = blob instanceof File
    ? blob
    : new File([blob], photo.originalFilename || 'photo.jpg', {
        type: photo.fileType || blob.type || 'image/jpeg',
      });

  return photoApi.uploadPhoto(
    photo.albumId,
    file,
    photo.caption,
  );
}

export async function uploadPhotosToAlbum(
  albumId: string,
  files: File[],
  onProgress?: photoApi.PhotoUploadProgressHandler,
): Promise<photoApi.PhotoUploadResult> {
  if (AUTH_MODE === 'mock') {
    throw new Error('중복 검사 업로드는 NAS API 모드에서 사용할 수 있습니다.');
  }
  return photoApi.uploadPhotosToAlbum(albumId, files, onProgress);
}

export async function autoOrganizePhotos(
  files: File[],
  onProgress?: photoApi.PhotoUploadProgressHandler,
): Promise<photoApi.PhotoUploadResult> {
  if (AUTH_MODE === 'mock') {
    throw new Error('날짜별 자동 분류는 NAS API 모드에서 사용할 수 있습니다.');
  }
  return photoApi.autoOrganizePhotos(files, onProgress);
}

export async function updatePhotoCaption(
  id: string,
  caption: string,
): Promise<PhotoDetail | undefined> {
  if (AUTH_MODE === 'mock') return photoMock.updatePhotoCaption(id, caption);
  return photoApi.updatePhotoCaption(id, caption);
}

export async function deletePhoto(id: string): Promise<boolean> {
  if (AUTH_MODE === 'mock') return photoMock.deletePhoto(id);
  return photoApi.deletePhoto(id);
}

export async function deletePhotosBatch(photoIds: string[]): Promise<{
  message: string;
  deletedIds: string[];
  deletedCount: number;
}> {
  if (AUTH_MODE === 'mock') {
    const deletedIds: string[] = [];
    for (const photoId of photoIds) {
      if (await photoMock.deletePhoto(photoId)) deletedIds.push(photoId);
    }
    return {
      message: `선택한 사진 ${deletedIds.length}장을 삭제했습니다.`,
      deletedIds,
      deletedCount: deletedIds.length,
    };
  }
  return photoApi.deletePhotosBatch(photoIds);
}
